/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useHttpServer.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    State management hook for the HTTP/HTTPS Server module.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNginxConfigStore } from '../../../store/nginxConfig';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import * as api from '../api/httpServerApi';
import type {
    HttpServerConfig,
    HttpServerFormState
} from '../types';

function configToFormState(data: HttpServerConfig): HttpServerFormState {
    return {
        httpPort: data.httpPort,
        httpsPort: data.httpsPort,
        bindAddress: data.bindAddress,
        enableHttp: data.enableHttp,
        enableHttps: data.enableHttps,
        forceHttps: data.forceHttps,
        tlsMinVersion: data.tlsMinVersion,
        tlsMaxVersion: data.tlsMaxVersion,
        cipherProfile: data.cipherProfile,
        hstsEnabled: data.hstsEnabled,
        hstsMaxAge: data.hstsMaxAge,
        hstsIncludeSubdomains: data.hstsIncludeSubdomains,
        hstsPreload: data.hstsPreload,
        defaultCertificateId: data.defaultCertificateId ?? null,
        corsOrigins: data.corsOrigins || [],
        accessLogEnabled: data.accessLogEnabled,
        errorLogLevel: data.errorLogLevel,
        maxRequestSize: data.maxRequestSize,
        rateLimitEnabled: data.rateLimitEnabled,
        rateLimitRequests: data.rateLimitRequests,
        rateLimitWindow: data.rateLimitWindow,
        disableTlsRenegotiation: data.disableTlsRenegotiation
    };
}

export function useHttpServer() {
    const { t } = useI18n();
    const { user } = useAuthStore();
    const { showSuccess, showError } = useNotifications();
    const tenantSlug = user?.tenant?.slug ?? 'main';

    // State
    const [config, setConfig] = useState<HttpServerConfig | null>(null);
    const [certificates, setCertificates] = useState<api.CertificateOption[]>([]);
    const [formData, setFormData] = useState<HttpServerFormState>({
        httpPort: 80,
        httpsPort: 443,
        bindAddress: '0.0.0.0',
        enableHttp: true,
        enableHttps: true,
        forceHttps: false,
        tlsMinVersion: '1.2',
        tlsMaxVersion: '1.3',
        cipherProfile: 'modern',
        hstsEnabled: false,
        hstsMaxAge: 31536000,
        hstsIncludeSubdomains: false,
        hstsPreload: false,
        defaultCertificateId: null,
        corsOrigins: [],
        accessLogEnabled: true,
        errorLogLevel: 'error',
        maxRequestSize: 10485760,
        rateLimitEnabled: false,
        rateLimitRequests: 100,
        rateLimitWindow: 60,
        disableTlsRenegotiation: true
    });
    const [originalData, setOriginalData] = useState<HttpServerFormState | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Load configuration & available certificates
    const loadConfig = useCallback(async () => {
        setLoading(true);
        try {
            const [response, certsList] = await Promise.all([
                api.getHttpServerConfig(tenantSlug, user?.id),
                api.getCertificates(tenantSlug, user?.id)
            ]);

            setCertificates(certsList);

            if (response.success && response.data) {
                setConfig(response.data);

                let defaultCertId = response.data.defaultCertificateId ?? null;

                const formState = configToFormState({
                    ...response.data,
                    defaultCertificateId: defaultCertId
                });
                setFormData(formState);
                setOriginalData(formState);
            }
        } catch (error) {
            console.error('Failed to load HTTP server config:', error);
            showError(t('httpServer.messages.loadError', 'Failed to load HTTP server configuration'));
        } finally {
            setLoading(false);
        }
    }, [tenantSlug, user?.id, showError, t]);

    // Save configuration
    const saveConfig = useCallback(async () => {
        setSaving(true);
        try {
            const response = await api.updateHttpServerConfig(formData, tenantSlug, user?.id);
            if (response.success) {
                setConfig(response.data);
                setOriginalData(formData);
                useNginxConfigStore.getState().markPendingChanges();
                showSuccess(t('httpServer.messages.saveSuccess', 'HTTP server configuration saved successfully'));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to save HTTP server config:', error);
            showError(error.message || t('httpServer.messages.saveError', 'Failed to save HTTP server configuration'));
            return false;
        } finally {
            setSaving(false);
        }
    }, [formData, tenantSlug, user?.id, showSuccess, showError, t]);

    // Reset configuration
    const resetConfig = useCallback(async () => {
        setSaving(true);
        try {
            const response = await api.resetHttpServerConfig(tenantSlug, user?.id);
            if (response.success && response.data) {
                setConfig(response.data);
                const formState = configToFormState(response.data);
                setFormData(formState);
                setOriginalData(formState);
                showSuccess(t('httpServer.messages.resetSuccess', 'HTTP server configuration reset to defaults'));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to reset HTTP server config:', error);
            showError(error.message || t('httpServer.messages.resetError', 'Failed to reset HTTP server configuration'));
            return false;
        } finally {
            setSaving(false);
        }
    }, [tenantSlug, user?.id, showSuccess, showError, t]);

    // Check for changes
    const hasChanges = useCallback(() => {
        if (!originalData) return false;
        return JSON.stringify(formData) !== JSON.stringify(originalData);
    }, [formData, originalData]);

    // Update form field
    const updateField = useCallback(<K extends keyof HttpServerFormState>(
        field: K,
        value: HttpServerFormState[K]
    ) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    // Cancel changes
    const cancelChanges = useCallback(() => {
        if (originalData) {
            setFormData(originalData);
        }
    }, [originalData]);

    // Load on mount
    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    return {
        config,
        formData,
        certificates,
        loading,
        saving,
        hasChanges: hasChanges(),
        updateField,
        saveConfig,
        resetConfig,
        cancelChanges,
        reload: loadConfig
    };
}
